import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import LogoAtivaJa from "@/components/LogoAtivaJa";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, MessageCircle, Star, ShieldAlert, CreditCard, CheckCircle2, Camera, Bookmark } from "lucide-react";

export default function ConexaoParticular() {
  const { conexaoId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [cx, setCx] = useState(null);
  const [verificando, setVerificando] = useState(!!sessionId);
  const [erro, setErro] = useState(false);
  const tentativas = useRef(0);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/particular/conexao/${conexaoId}`);
      setCx(r.data);
      return r.data;
    } catch { setErro(true); return null; }
  };

  useEffect(() => {
    const poll = async () => {
      const dados = await load();
      if (!dados) return;
      if (sessionId && dados.status === "aguardando_pagamento") {
        const check = async () => {
          tentativas.current += 1;
          try {
            const r = await axios.get(`${API}/particular/conexao/${conexaoId}/pagamento-status`);
            if (r.data.status_conexao !== "aguardando_pagamento") {
              setVerificando(false);
              load();
              return;
            }
          } catch { /* segue tentando */ }
          if (tentativas.current < 10) setTimeout(check, 2000);
          else { setVerificando(false); toast.error("Não conseguimos confirmar o pagamento. Tente novamente."); }
        };
        check();
      } else {
        setVerificando(false);
      }
    };
    poll();
  }, [conexaoId, sessionId]);

  const pagarNovamente = async () => {
    try {
      const r = await axios.post(`${API}/particular/conexao/${conexaoId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (erro) return (
    <Shell><div className="text-center py-16" data-testid="conexao-nao-encontrada">
      <div className="font-display font-extrabold text-2xl text-[#0F172A]">Conexão não encontrada</div>
      <Link to="/montadores" className="btn-aj-primary inline-flex mt-6">Nova busca</Link>
    </div></Shell>
  );
  if (!cx || verificando) return (
    <Shell><div className="text-center py-16 text-[#64748B]" data-testid="conexao-verificando">
      {verificando ? "Confirmando seu pagamento…" : "Carregando…"}
    </div></Shell>
  );

  return (
    <Shell>
      {cx.status === "aguardando_pagamento" && (
        <div className="aj-card p-6 border-l-4 border-l-[#F59E0B] aj-fade-up" data-testid="conexao-pendente">
          <div className="font-display font-extrabold text-xl text-[#0F172A]">Pagamento pendente</div>
          <p className="text-sm text-[#64748B] mt-1">Finalize o pagamento de R$ 10,00 para liberar os contatos.</p>
          <button onClick={pagarNovamente} className="btn-aj-primary mt-4" data-testid="conexao-pagar-agora">
            <CreditCard size={16} /> Pagar R$ 10,00
          </button>
        </div>
      )}
      {cx.status === "pago" && <FormPedido conexaoId={conexaoId} onDone={load} />}
      {cx.status === "liberado" && <Liberado cx={cx} />}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="conexao-logo-link"><LogoAtivaJa size="sm" /></Link>
          <Link to="/montadores" className="text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors">Nova busca</Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10">{children}</main>
    </div>
  );
}

function FormPedido({ conexaoId, onDone }) {
  const [f, setF] = useState({ nome: "", telefone: "", endereco: "", produto: "", tipo_servico: "montagem", observacao: "" });
  const [foto, setFoto] = useState(null);
  const [loading, setLoading] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (foto) fd.append("foto", foto);
      await axios.post(`${API}/particular/conexao/${conexaoId}/pedido`, fd);
      toast.success("Contatos liberados!");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="aj-fade-up">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#059669]">
        <CheckCircle2 size={14} /> Pagamento confirmado
      </div>
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A] mt-2">
        Conte sobre o <span className="aj-accent-orange">seu serviço</span>
      </h1>
      <p className="text-[#64748B] mt-2 text-sm">
        Essas informações ajudam os montadores a entender o serviço. Você <strong>não precisa informar valores</strong> —
        negocie diretamente com o montador.
      </p>
      <form onSubmit={enviar} className="mt-6 aj-card p-6 sm:p-7 space-y-4" data-testid="conexao-form-pedido">
        <FF label="Seu nome" v={f.nome} on={(v) => setF(x => ({...x, nome: v}))} testid="pedido-nome" required />
        <FF label="Seu telefone/WhatsApp" v={f.telefone} on={(v) => setF(x => ({...x, telefone: v}))} testid="pedido-telefone" required />
        <FF label="Endereço completo" v={f.endereco} on={(v) => setF(x => ({...x, endereco: v}))} testid="pedido-endereco" required />
        <FF label="Produto (ex.: guarda-roupa 6 portas)" v={f.produto} on={(v) => setF(x => ({...x, produto: v}))} testid="pedido-produto" required />
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-[#142766]">Tipo de serviço</Label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[["montagem","Montagem"],["desmontagem","Desmontagem"],["ambos","Ambos"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setF(x => ({...x, tipo_servico: v}))}
                className={`h-11 rounded-lg text-sm font-semibold border transition-colors ${
                  f.tipo_servico === v ? "bg-[#142766] border-[#142766] text-white" : "bg-white border-[#CBD5E1] text-[#334155] hover:border-[#142766]"
                }`} data-testid={`pedido-tipo-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-[#142766]">Foto do produto (opcional · 1 foto)</Label>
          <label className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-[#CBD5E1] p-4 cursor-pointer hover:border-[#FF6700] transition-colors" data-testid="pedido-foto-label">
            <Camera size={20} className="text-[#FF6700]" />
            <span className="text-sm text-[#64748B]">{foto ? foto.name : "Clique para anexar (máx. 5MB)"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="pedido-foto-input"
              onChange={(e) => setFoto(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-[#142766]">Observação (quais serviços, detalhes)</Label>
          <Textarea rows={3} value={f.observacao} onChange={(e) => setF(x => ({...x, observacao: e.target.value}))}
            className="mt-2 rounded-lg" placeholder="Ex.: 2 guarda-roupas e 1 cômoda, apartamento sem elevador…" data-testid="pedido-observacao" />
        </div>
        <button type="submit" disabled={loading} className="btn-aj-primary w-full h-12 justify-center disabled:opacity-60" data-testid="pedido-enviar">
          {loading ? "Liberando contatos…" : "Liberar contatos dos montadores"}
        </button>
      </form>
    </div>
  );
}

function Liberado({ cx }) {
  const [aba, setAba] = useState("montadores");
  return (
    <div className="aj-fade-up" data-testid="conexao-liberada">
      <div className="aj-eyebrow">Conexão ativa</div>
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A] mt-2">
        Seus <span className="aj-accent-orange">{cx.montadores.length} montador(es)</span>
      </h1>
      <p className="text-[#64748B] mt-2 text-sm">
        Ligue ou chame no WhatsApp e negocie diretamente. <strong>Salve este link</strong> para acessar novamente.
      </p>

      <div className="mt-3 rounded-lg bg-[#FFF3E0] border border-[#FDBA74] p-3 flex items-center gap-2 text-xs text-[#E05C00] font-semibold" data-testid="conexao-salvar-link">
        <Bookmark size={14} /> Guarde este endereço: esta página é o seu comprovante e acesso aos contatos.
      </div>

      <div className="mt-6 flex gap-2 border-b border-[#E2E8F0]">
        {[["montadores", "Montadores"], ["pedido", "Meu pedido"], ["termo", "Termo aceito"]].map(([v, l]) => (
          <button key={v} onClick={() => setAba(v)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              aba === v ? "border-[#FF6700] text-[#FF6700]" : "border-transparent text-[#64748B] hover:text-[#142766]"
            }`} data-testid={`conexao-aba-${v}`}>{l}</button>
        ))}
      </div>

      {aba === "montadores" && (
        <div className="mt-5 space-y-3">
          {cx.montadores.map((m, i) => (
            <div key={m.id} className="aj-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`conexao-montador-${i}`}>
              <div>
                <div className="font-display font-bold text-lg text-[#0F172A]">{m.nome}</div>
                <div className="flex items-center gap-2 text-xs text-[#64748B] mt-0.5">
                  {m.score != null && (
                    <span className="inline-flex items-center gap-1 text-[#B45309] font-bold">
                      <Star size={12} className="fill-[#F59E0B] text-[#F59E0B]" /> {Number(m.score).toFixed(0)}
                    </span>
                  )}
                  {m.cidade && <span>{m.cidade}{m.bairro ? ` · ${m.bairro}` : ""}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <a href={`tel:${m.telefone}`} className="h-10 px-4 rounded-lg border-[1.5px] border-[#142766] text-[#142766] text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[#142766] hover:text-white transition-colors" data-testid={`montador-ligar-${i}`}>
                  <Phone size={14} /> {m.telefone}
                </a>
                <a href={`https://wa.me/55${String(m.whatsapp || m.telefone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                  className="h-10 px-4 rounded-lg bg-[#10B981] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:brightness-105 transition-[filter]" data-testid={`montador-whats-${i}`}>
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {aba === "pedido" && cx.pedido && (
        <div className="mt-5 aj-card p-6 text-sm space-y-1.5 text-[#334155]" data-testid="conexao-pedido-resumo">
          <div><span className="font-semibold text-[#142766]">Nome:</span> {cx.pedido.nome}</div>
          <div><span className="font-semibold text-[#142766]">Telefone:</span> {cx.pedido.telefone}</div>
          <div><span className="font-semibold text-[#142766]">Endereço:</span> {cx.pedido.endereco}</div>
          <div><span className="font-semibold text-[#142766]">Produto:</span> {cx.pedido.produto}</div>
          <div><span className="font-semibold text-[#142766]">Serviço:</span> <span className="capitalize">{cx.pedido.tipo_servico}</span></div>
          {cx.pedido.observacao && <div><span className="font-semibold text-[#142766]">Observação:</span> {cx.pedido.observacao}</div>}
          {cx.pedido.tem_foto && (
            <img src={`${API}/particular/conexao/${cx.id}/foto`} alt="Produto"
              className="mt-3 rounded-lg border border-[#E2E8F0] max-h-64 object-contain" data-testid="conexao-pedido-foto" />
          )}
        </div>
      )}

      {aba === "termo" && (
        <div className="mt-5 aj-card p-6" data-testid="conexao-termo-registro">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-3">
            <ShieldAlert size={14} /> Registro do aceite
          </div>
          <div className="text-xs text-[#64748B] space-y-1 mb-4">
            <div>Aceito em: <strong>{cx.aceite?.em ? new Date(cx.aceite.em).toLocaleString("pt-BR") : "—"}</strong></div>
            <div>IP: <span className="font-mono">{cx.aceite?.ip || "—"}</span> · Versão do termo: <span className="font-mono">{cx.aceite?.versao}</span></div>
          </div>
          <pre className="whitespace-pre-wrap text-xs text-[#334155] font-body leading-relaxed bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
{cx.aceite?.texto}
          </pre>
        </div>
      )}
    </div>
  );
}

function FF({ label, v, on, testid, required }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-[#142766]">{label}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} className="mt-2 rounded-lg" data-testid={testid} required={required} />
    </div>
  );
}
